import type { IMccRepository, MccCode, MccOption } from '@app/domain/ports';
import type { IHttpClient } from '@app/domain/ports';

/**
 * MCC Repository implementation
 * Loads Merchant Category Code reference data from JSON file via HTTP
 *
 * Unlike other repositories, this doesn't use SQLite - MCC codes are immutable
 * reference data that don't need database persistence.
 */
export class MccRepository implements IMccRepository {
  private mccOptionsCache: MccOption[] | null = null;
  private loadingPromise: Promise<void> | null = null;

  constructor(private readonly http: IHttpClient) {}

  async search(query: string): Promise<MccOption[]> {
    await this.ensureLoaded();

    if (!query || query.length < 2) {
      return [];
    }

    const lowerQuery = query.toLowerCase();
    const options = this.getMccOptions();

    return options.filter(
      (option) => option.code.toString().includes(query) || option.description.toLowerCase().includes(lowerQuery),
    );
  }

  async getDescription(code: number): Promise<string | undefined> {
    await this.ensureLoaded();
    return this.mccOptionsCache?.find((opt) => opt.code === code)?.description;
  }

  async getOption(code: number): Promise<MccOption | undefined> {
    await this.ensureLoaded();
    return this.mccOptionsCache?.find((opt) => opt.code === code);
  }

  /**
   * Ensure MCC codes are loaded (idempotent, handles concurrent calls)
   */
  private async ensureLoaded(): Promise<void> {
    if (this.mccOptionsCache !== null) {
      return; // Already loaded
    }

    if (this.loadingPromise) {
      // Already loading, wait for existing promise
      return this.loadingPromise;
    }

    // Start loading
    this.loadingPromise = this.loadData();
    try {
      await this.loadingPromise;
    } finally {
      this.loadingPromise = null; // Clear after completion (success or failure)
    }
  }

  private async loadData(): Promise<void> {
    try {
      const codes = await this.http.get<MccCode[]>('/assets/data/mcc-codes.json');
      // Transform immediately to MccOption format (only store what we need)
      this.mccOptionsCache = codes.map((mcc) => ({
        code: parseInt(mcc.mcc, 10),
        description: mcc.edited_description,
        label: `${mcc.mcc} - ${mcc.edited_description}`,
      }));
      console.log(`[MccRepository] Loaded ${codes.length} MCC codes`);
    } catch (error) {
      console.error('[MccRepository] Failed to load MCC codes:', error);
      throw error;
    }
  }

  /**
   * Get MCC options (already loaded and cached)
   */
  private getMccOptions(): MccOption[] {
    return this.mccOptionsCache ?? [];
  }
}
